import { type ReferralStage, referralStageLabel } from '@commissionsight/shared';
import { useEffect, useState } from 'react';
import { useAuth } from '../api.js';
import { Button, Card, Loading, inputClass } from './ui.js';

interface ReferralRow {
  company: string | null;
  stage: ReferralStage;
  createdAt: number | string;
  creditedAt: number | string | null;
  creditCents: number | null;
}
interface ReferralsView {
  code: string;
  link: string;
  creditBalanceCents: number;
  creditedThisYear: number;
  capPerYear: number;
  referrals: ReferralRow[];
}

const usd = (cents: number) => `$${(cents / 100).toFixed(2)}`;
const shortDate = (v: number | string) =>
  new Date(v).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

// Stage → pill classes (the further along, the greener).
const STAGE_CLASS: Record<ReferralStage, string> = {
  signed_up: 'bg-neutral-100 text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300',
  statements: 'bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300',
  payment: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  qualified: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300',
  credited: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
};

/** Profile "Referral program" card: shareable link + earned credit + referrals. */
export function ReferralCard() {
  const { token, baseUrl } = useAuth();
  const [view, setView] = useState<ReferralsView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!token) return;
    let live = true;
    fetch(`${baseUrl.replace(/\/$/, '')}/referrals`, {
      headers: { authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((v: ReferralsView) => {
        if (live) setView(v);
      })
      .catch(() => live && setError('Could not load your referrals.'));
    return () => {
      live = false;
    };
  }, [token, baseUrl]);

  const copy = async () => {
    if (!view) return;
    try {
      await navigator.clipboard.writeText(view.link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard blocked — the link is selectable in the input as a fallback */
    }
  };

  return (
    <Card>
      <h2 className="mb-1 font-semibold">Referral program</h2>
      <p className="mb-4 text-sm text-neutral-500">
        Share your link. When an agency you refer signs up, uploads at least 2 statements, and adds
        a payment method, you earn a <span className="font-medium">$25 credit</span> toward your
        bill — up to 10 per year.
      </p>

      {error && <p className="text-sm text-rose-600">{error}</p>}
      {!view && !error && <Loading label="Loading your referrals…" />}

      {view && (
        <div className="space-y-5">
          <div>
            <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-neutral-500">
              Your referral link
            </span>
            <div className="flex items-center gap-2">
              <input
                className={inputClass}
                value={view.link}
                readOnly
                onFocus={(e) => e.currentTarget.select()}
              />
              <Button variant="ghost" onClick={copy}>
                {copied ? 'Copied ✓' : 'Copy'}
              </Button>
            </div>
          </div>

          <div className="flex flex-wrap gap-3">
            <div className="rounded-xl border border-neutral-200 px-4 py-3 dark:border-neutral-800">
              <div className="text-xs uppercase tracking-wide text-neutral-500">Credit balance</div>
              <div className="text-lg font-semibold text-emerald-600">
                {usd(view.creditBalanceCents)}
              </div>
            </div>
            <div className="rounded-xl border border-neutral-200 px-4 py-3 dark:border-neutral-800">
              <div className="text-xs uppercase tracking-wide text-neutral-500">
                Earned this year
              </div>
              <div className="text-lg font-semibold">
                {view.creditedThisYear}{' '}
                <span className="text-sm font-normal text-neutral-400">of {view.capPerYear}</span>
              </div>
            </div>
          </div>

          {view.referrals.length === 0 ? (
            <p className="text-sm text-neutral-500">
              No referrals yet — share your link to get started.
            </p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-left text-neutral-500">
                <tr>
                  <th className="py-1 font-medium">Agency</th>
                  <th className="font-medium">Stage</th>
                  <th className="text-right font-medium">Joined</th>
                </tr>
              </thead>
              <tbody>
                {view.referrals.map((ref, i) => (
                  <tr
                    key={`${ref.company}-${i}`}
                    className="border-t border-neutral-100 dark:border-neutral-800"
                  >
                    <td className="py-2">{ref.company ?? '—'}</td>
                    <td>
                      <span
                        className={`rounded-full px-2 py-0.5 text-xs font-medium ${STAGE_CLASS[ref.stage]}`}
                      >
                        {referralStageLabel(ref.stage)}
                      </span>
                    </td>
                    <td className="text-right text-neutral-500">{shortDate(ref.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </Card>
  );
}
